import {Injectable} from '@angular/core';
import {BehaviorSubject, merge, Observable} from 'rxjs';
import {FlatTreeControl} from '@angular/cdk/tree';
import {CollectionViewer, DataSource, SelectionChange} from '@angular/cdk/collections';
import {finalize, map} from 'rxjs/operators';
import {SubSink} from '@Common/core/utils/subsink';
import {DataBaseTreeModel} from './data-base-tree.model';

/**
 * DataSource pour le tree lazy loading
 */
@Injectable()
export class DynamicDataSource<T extends DataBaseTreeModel> implements DataSource<T> {
  private _dataChange = new BehaviorSubject<T[]>([]);
  get data(): T[] { return this._dataChange.value; }
  set data(value: T[]) {
    this.treeCodesControl.dataNodes = value;
    this._dataChange.next(value);
  }
  /**
   * Service à utiliser pour fctLoadChildren ou fctLoadParents
   */
  private _svcLoad;
  set svcLoad(value) {
    this._svcLoad = value;
  }
  /**
   * Fonction de chargement API de la racine / des pères
   */
  private _fctLoadParents: (id: string) => Observable<any>;
  set fctLoadParents(value) {
    this._fctLoadParents = value;
  }
  /**
   * Argument pour l'appel de fctLoadParents, optionnel
   * @param {any | any[]} value : une valeur ou un tableau de valeurs à passer à la fonction, chaque valeur du tableau correspond à chaque argument de la fonction fctLoadChildren
   * @private
   */
  private _argLoadParents;
  set argLoadParents(value) {
    this._argLoadParents = value;
  }
  /**
   * Fonction de chargement API des enfants d'un père représenté par id
   */
  private _fctLoadChildren: (id: string) => Observable<any>;
  set fctLoadChildren(value) {
    this._fctLoadChildren = value;
  }
  /**
   * Argument supplémentaire pour l'appel de fctLoadParents, optionnel
   * @param {any | any[]} value : une valeur ou un tableau de valeurs à passer à la fonction, chaque valeur du tableau correspond à chaque argument de la fonction fctLoadChildren
   * @private
   */
  private _argLoadChildren;
  set argLoadChildren(value) {
    this._argLoadChildren = value;
  }
  /**
   * Fonction de mapping modèle ramené par fctLoadChildren ou fctLoadParent en T[]
   */
  private _mapFunction: (DataTypeModel, []) => T[];
  set mapModelFunction(value) {
    this._mapFunction = value;
  }
  private _conditionExpand: (T) => boolean;
  set conditionExpand(fct) {
    this._conditionExpand = fct;
  }
  subSink = new SubSink();
  constructor(private treeCodesControl: FlatTreeControl<T>) {}

  /**
   * Initialisation du tree des parents
   */
  initTree() {
    if (this._svcLoad && this._fctLoadParents && this._mapFunction) {
      let callFun;
      if (Array.isArray(this._argLoadParents)) {
        callFun = this._fctLoadParents.call(this._svcLoad, ...this._argLoadParents);
      } else {
        callFun = this._fctLoadParents.call(this._svcLoad, this._argLoadParents);
      }
      this.subSink.sink = callFun.subscribe((_data) => {
          if (_data && _data.length > 0) {
            this.data = this._mapFunction(null, _data);
          }
        });
    }
  }
  connect(collectionViewer: CollectionViewer): Observable<T[]> {
    this.treeCodesControl.expansionModel.changed.subscribe(change => {
      if ((change as SelectionChange<T>).added ||
          (change as SelectionChange<T>).removed) {
        this.handleCodeTreeControl(change as SelectionChange<T>);
      }
    });
    return merge(collectionViewer.viewChange, this._dataChange).pipe(map(() => this.data));
  }
  disconnect(collectionViewer: CollectionViewer): void {}
  /**
   * +/- sur les éléments pères
   * @param change
   */
  handleCodeTreeControl(change: SelectionChange<T>) {
    if (change.added) {
      change.added.forEach(node => this.toggleNode(node, true));
    }
    if (change.removed) {
      change.removed.slice().reverse().forEach(node => this.toggleNode(node, false));
    }
  }
  /**
   * Chargement des fils lors de l'extension d'un père
   * @param {T} node : le noeud parent cliqué
   * @param {number} index : indice du père qui a été cliqué
   * @private
   */
  private _getChildrenData(node: T, index: number) {
    node.isLoading = true;
    let callFun;
    if (this._argLoadChildren) {
      if (Array.isArray(this._argLoadChildren)) {
        callFun = this._fctLoadChildren.call(this._svcLoad, node.id.toString(), ...this._argLoadChildren);
      } else {
        callFun = this._fctLoadChildren.call(this._svcLoad, node.id.toString(), this._argLoadChildren);
      }
    } else {
      callFun = this._fctLoadChildren.call(this._svcLoad, node.id.toString());
    }
    /*const callFun = this._argLoadChildren
      ? this._fctLoadChildren.call(this._svcLoad, node.id.toString(), this._argLoadChildren)
      : this._fctLoadChildren.call(this._svcLoad, node.id.toString());*/
    this.subSink.sink = callFun
      .pipe(finalize(() => node.isLoading = false))
      .subscribe((_data) => {
        if (_data && _data.length > 0) {
          const nodes = this._mapFunction(node, _data);
          if (this._conditionExpand) {
            nodes.forEach(_node => _node.expandable = this._conditionExpand(_node));
          }
          this.data.splice(index + 1, 0, ...nodes);
          this._dataChange.next(this.data);
        }
      });
  }
  /**
   * Chargement des données enfants ou réduction
   * @param node
   * @param expand
   */
  toggleNode(node: T, expand: boolean) {
    const index = this.data.indexOf(node);
    if (expand && node.hasChildren) {
      node.expandable = true;
      this._getChildrenData(node, index);
    }
    if (!expand) {
      node.expandable = false;
      let count = 0;
      for (let i = index + 1; i < this.data.length && this.data[i].level > node.level; i++, count++) {}
      this.data.splice(index + 1, count);
      this._dataChange.next(this.data);
    }
  }
}
